import React, { useState, useEffect } from 'react';
import axios from 'axios';

function ClassStructureManager({ onClose }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClass, setEditingClass] = useState(null);
  const [newClassName, setNewClassName] = useState('');

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get('/api/classes');
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClass = (classItem) => {
    setEditingClass(classItem._id);
    setNewClassName(classItem.name);
  };

  const handleSaveEdit = async (classId) => {
    if (!newClassName.trim()) {
      alert('Class name cannot be empty');
      return;
    }

    try {
      await axios.put(`/api/classes/${classId}`, { name: newClassName.trim() });
      await fetchClasses();
      setEditingClass(null);
      setNewClassName('');
      alert('Class name updated successfully!');
    } catch (error) {
      console.error('Error updating class:', error);
      alert('Error updating class name');
    }
  };

  const handleDeleteClass = async (classItem) => {
    if (!window.confirm(`Are you sure you want to delete class "${classItem.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/classes/${classItem._id}`);
      await fetchClasses();
      alert('Class deleted successfully!');
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Error deleting class');
    }
  };

  const handleAddClass = async () => {
    const className = prompt('Enter new class name (e.g., 12C):');
    if (className && className.trim()) {
      try {
        await axios.post('/api/classes', { name: className.trim() });
        await fetchClasses();
        alert(`Class ${className} added successfully!`);
      } catch (error) {
        console.error('Error adding class:', error);
        if (error.response?.data?.message?.includes('duplicate')) {
          alert('Class already exists!');
        } else {
          alert('Error adding class');
        }
      }
    }
  };

  const getClassStats = async (className) => {
    try {
      const response = await axios.get(`/api/users?role=student&class=${className}`);
      return response.data.length;
    } catch (error) {
      return 0;
    }
  };

  if (loading) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
        <div style={{ background: 'white', padding: '30px', borderRadius: '15px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '15px' }}>🏫</div>
          <div>Loading classes...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', width: '90%', maxWidth: '800px', height: '80%', borderRadius: '15px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
        
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', color: 'white', padding: '20px', textAlign: 'center', position: 'relative' }}>
          <button 
            onClick={onClose}
            style={{ position: 'absolute', right: '20px', top: '20px', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', fontSize: '18px' }}
          >
            ✕
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '15px' }}>
            <div style={{ width: '60px', height: '60px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
              🏫
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>Class Structure Management</h2>
              <p style={{ margin: '5px 0 0 0', fontSize: '16px', opacity: 0.9 }}>Edit, Change & Delete Class Names</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
          
          {/* Add New Class Button */}
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <button 
              onClick={handleAddClass}
              style={{ 
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
                color: 'white', 
                border: 'none', 
                padding: '12px 24px', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontSize: '16px', 
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
              }}
            >
              ➕ Add New Class
            </button>
          </div>

          {/* Classes List */}
          <div style={{ background: 'white', border: '2px solid #2196f3', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)' }}>
            <div style={{ background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', color: 'white', padding: '15px', textAlign: 'center' }}>
              <h4 style={{ margin: 0, fontSize: '18px' }}>🏫 Existing Classes</h4>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>Manage all class names and structure</p>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '15px 10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '80px', fontSize: '14px', fontWeight: 'bold', color: '#495057' }}>S.No</th>
                    <th style={{ padding: '15px 10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: 'bold', color: '#495057' }}>Class Name</th>
                    <th style={{ padding: '15px 10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '120px', fontSize: '14px', fontWeight: 'bold', color: '#495057' }}>Students</th>
                    <th style={{ padding: '15px 10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '200px', fontSize: '14px', fontWeight: 'bold', color: '#495057' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((classItem, index) => (
                    <tr key={classItem._id} style={{ borderBottom: '1px solid #dee2e6', background: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                      <td style={{ padding: '15px 10px', textAlign: 'center', fontWeight: 'bold', color: '#2196f3', fontSize: '16px' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '15px 10px', fontSize: '16px' }}>
                        {editingClass === classItem._id ? (
                          <input
                            type="text"
                            value={newClassName}
                            onChange={(e) => setNewClassName(e.target.value)}
                            style={{ 
                              width: '100%', 
                              padding: '8px 12px', 
                              border: '2px solid #2196f3', 
                              borderRadius: '6px', 
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(classItem._id);
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <span style={{ fontWeight: 'bold', color: '#495057' }}>{classItem.name}</span>
                        )}
                      </td>
                      <td style={{ padding: '15px 10px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
                        <ClassStudentCount className={classItem.name} />
                      </td>
                      <td style={{ padding: '15px 10px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          {editingClass === classItem._id ? (
                            <>
                              <button 
                                onClick={() => handleSaveEdit(classItem._id)}
                                style={{ 
                                  background: '#28a745', 
                                  color: 'white', 
                                  border: 'none', 
                                  padding: '6px 12px', 
                                  borderRadius: '4px', 
                                  cursor: 'pointer', 
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                                title="Save changes"
                              >
                                ✓ Save
                              </button>
                              <button 
                                onClick={() => {
                                  setEditingClass(null);
                                  setNewClassName('');
                                }}
                                style={{ 
                                  background: '#6c757d', 
                                  color: 'white', 
                                  border: 'none', 
                                  padding: '6px 12px', 
                                  borderRadius: '4px', 
                                  cursor: 'pointer', 
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                                title="Cancel editing"
                              >
                                ✕ Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                onClick={() => handleEditClass(classItem)}
                                style={{ 
                                  background: '#ffc107', 
                                  color: '#212529', 
                                  border: 'none', 
                                  padding: '6px 12px', 
                                  borderRadius: '4px', 
                                  cursor: 'pointer', 
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                                title="Edit class name"
                              >
                                ✏️ Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteClass(classItem)}
                                style={{ 
                                  background: '#dc3545', 
                                  color: 'white', 
                                  border: 'none', 
                                  padding: '6px 12px', 
                                  borderRadius: '4px', 
                                  cursor: 'pointer', 
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                                title="Delete class"
                              >
                                🗑️ Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {classes.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>🏫</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>No Classes Found</div>
                <div style={{ fontSize: '14px' }}>Click "Add New Class" to create your first class</div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ background: '#f8f9fa', padding: '15px', borderTop: '2px solid #dee2e6', textAlign: 'center' }}>
          <div style={{ color: '#6c757d', fontSize: '12px' }}>
            Total Classes: {classes.length} | Admin Level 1 Exclusive Control
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component to show student count for each class
function ClassStudentCount({ className }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await axios.get(`/api/users?role=student&class=${className}`);
        setCount(response.data.length);
      } catch (error) {
        setCount(0);
      }
    };
    fetchCount();
  }, [className]);

  return (
    <span style={{ 
      background: count > 0 ? '#28a745' : '#6c757d', 
      color: 'white', 
      padding: '4px 8px', 
      borderRadius: '12px', 
      fontSize: '12px',
      fontWeight: 'bold'
    }}>
      {count} students
    </span>
  );
}

export default ClassStructureManager;